/**
 * @fileOverview Geometry for the per-view walkthrough — finding a step's element inside the frame,
 * deciding whether it may be pointed at, and bringing it into view. Pure DOM: no Vue, no i18n.
 * See docs/view-help.md.
 *
 * Everything here takes the frame's own `Window`, never the global one. The walkthrough explains the
 * real product running in an iframe, so "the viewport" means the frame's viewport and "the scroll
 * container" means one inside the frame's document. Reaching for the host's `window` here would
 * measure the wrong box every time.
 *
 * **Why the visibility test is as fussy as it is.** A DOM-anchored tour was built once and deleted
 * the next day (`b2fc41eb`); its post-mortem is in docs/onboarding-tour.md. Its guard was
 * `rect.width > 0 && rect.height > 0`, written believing an off-canvas drawer reports `0×0`. It does
 * not: Vuetify lays a closed `temporary` drawer out at full size and pushes it aside with a
 * `transform`, so it reports a healthy `283×44` box at `left: -301px`. Every step pointing at
 * navigation "found" its target and highlighted empty screen, and the tour's skip-the-step safety
 * net never fired once, because from its point of view nothing was ever missing.
 *
 * The frame is sized by us, so that particular trap cannot spring any more. {@link isAnchorVisible}
 * stays anyway: a region scrolled out of the frame's content column is the same class of mistake,
 * and it is the thing that makes skipping work.
 */

import type { ViewHelpAnchor } from "@/services/viewHelp/types"

/** Floor under which an element is too small to be worth pointing at. */
const MIN_ANCHOR_SIZE = 8

/** How much of an element must be inside its container before a step may point at it. */
const MIN_VISIBLE_SLIVER = 24

/** …and how much of its area, for elements small enough for the fraction to mean something. */
const MIN_VISIBLE_AREA_RATIO = 0.4

/** Comfort margin when scrolling a target in, from `marketing/lib/pointer.mjs`. */
const SCROLL_MARGIN = 24

/**
 * The room a step's region keeps below it when it is scrolled in, as a share of what the reader sees of
 * the scroller and at most a text's height. A region brought only just inside the bottom edge had its
 * dot and its text squeezed against the bottom of the frame, with the page able to scroll on.
 */
const TEXT_ROOM_SHARE = 0.25
const TEXT_ROOM_MAX = 180

/** How a region is brought into view: an action's target just inside, a step's region with room below. */
export interface ScrollRoom {
  roomBelow?: boolean
}

/** How far down a scroller's top edge is probed for something fixed over it, and in what steps. */
const COVER_PROBE_MAX = 200
const COVER_PROBE_STEP = 4

/** How long {@link waitForStableRect} keeps polling before giving up and measuring anyway. */
const STABLE_TIMEOUT_MS = 500

/** Two consecutive identical boxes count as settled — one frame of stillness. */
const STABLE_FRAMES = 2

/** How long {@link waitForFadeIn} waits for a fade on the way to an anchor before measuring anyway. */
const FADE_TIMEOUT_MS = 600

export interface Rect {
  top: number
  left: number
  width: number
  height: number
}

/** Why an anchor was refused. Surfaced in dev warnings so a broken step names its own fault. */
export type AnchorRejection = "detached" | "hidden" | "too-small" | "off-viewport" | "off-scroller" | null

const area = (rect: Rect) => Math.max(0, rect.width) * Math.max(0, rect.height)

const toRect = (box: DOMRect): Rect => ({ top: box.top, left: box.left, width: box.width, height: box.height })

const intersect = (a: Rect, b: Rect): Rect => {
  const left = Math.max(a.left, b.left)
  const top = Math.max(a.top, b.top)
  return {
    left,
    top,
    width: Math.min(a.left + a.width, b.left + b.width) - left,
    height: Math.min(a.top + a.height, b.top + b.height) - top,
  }
}

const viewportRect = (frame: Window): Rect => ({
  top: 0,
  left: 0,
  width: frame.innerWidth,
  height: frame.innerHeight,
})

/**
 * The selector for a catalog anchor id.
 *
 * The catalog stores **ids, not selectors**, and this is the only place they become one. A catalog
 * that could name arbitrary CSS would let a step reach for `.v-card:nth-child(3)` and quietly break
 * the next time that markup moved; an id has to be written on the element on purpose.
 */
export const anchorSelector = (id: string) => `[data-help="${CSS.escape(id)}"]`

/**
 * Every element a step points at that is currently in the frame's document. **Not** filtered for
 * visibility — being in the DOM and being pointable are different questions, and the second one is
 * {@link isAnchorVisible}'s.
 */
export function findAnchors(frame: Window | null, anchor: ViewHelpAnchor): HTMLElement[] {
  if (!frame?.document || !anchor) return []
  const ids = Array.isArray(anchor) ? anchor : [anchor]
  return ids.flatMap(id => Array.from(frame.document.querySelectorAll<HTMLElement>(anchorSelector(id))))
}

/**
 * The element that actually scrolls `el`, or null if the frame's document does.
 *
 * Both axes, unlike the version in `marketing/lib/pointer.mjs` this is adapted from: the review
 * table sits in a horizontally scrolling `.review__scroll`, and a row scrolled out sideways is just
 * as unpointable as one scrolled out downwards.
 */
export function resolveScroller(frame: Window, el: HTMLElement): HTMLElement | null {
  const doc = frame.document
  let node = el.parentElement
  while (node && node !== doc.body && node !== doc.documentElement) {
    const style = frame.getComputedStyle(node)
    const scrollsY = /(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 4
    const scrollsX = /(auto|scroll|overlay)/.test(style.overflowX) && node.scrollWidth > node.clientWidth + 4
    if (scrollsY || scrollsX) return node
    node = node.parentElement
  }
  return null
}

/**
 * Whether enough of `container` shows `rect` for a reader to see what is being pointed at.
 *
 * The area test is skipped along an axis on which the element is larger than its container, or a
 * table taller than the frame — which is most of them on these pages — would be refused for the
 * crime of being long.
 */
const showsEnough = (rect: Rect, container: Rect): boolean => {
  const visible = intersect(rect, container)
  if (visible.width <= 0 || visible.height <= 0) return false
  if (visible.width < Math.min(rect.width, MIN_VISIBLE_SLIVER)) return false
  if (visible.height < Math.min(rect.height, MIN_VISIBLE_SLIVER)) return false

  const overflowsContainer = rect.height > container.height || rect.width > container.width
  if (overflowsContainer) return true
  return area(visible) >= MIN_VISIBLE_AREA_RATIO * area(rect)
}

/** Why this element may not be pointed at, or `null` if it may. Five tests, cheapest first. */
export function anchorRejection(frame: Window, el: HTMLElement): AnchorRejection {
  if (!el.isConnected) return "detached"
  if (el.closest("[hidden]") || el.closest('[aria-hidden="true"]')) return "hidden"

  // Catches `display: none`, `visibility: hidden` and `opacity: 0` — and nothing about position.
  // Optional-chained because it is not in every engine we support.
  if (el.checkVisibility?.({ checkOpacity: true, checkVisibilityCSS: true } as never) === false) return "hidden"

  const rect = toRect(el.getBoundingClientRect())
  if (rect.width < MIN_ANCHOR_SIZE || rect.height < MIN_ANCHOR_SIZE) return "too-small"
  if (!showsEnough(rect, viewportRect(frame))) return "off-viewport"

  const scroller = resolveScroller(frame, el)
  if (scroller && !showsEnough(rect, toRect(scroller.getBoundingClientRect()))) return "off-scroller"

  return null
}

/** {@link anchorRejection} as a predicate. */
export const isAnchorVisible = (frame: Window, el: HTMLElement): boolean => anchorRejection(frame, el) === null

/**
 * The first line of `view` from the top that shows the scroller itself rather than something fixed
 * over it — the app bar, above all. Found by asking the document what is drawn there, so it holds for
 * whatever covers the edge, without naming it.
 *
 * Without it, "just inside the top edge" was just inside the scroller's *box*: the app's content
 * column starts at the very top of the window and the app bar is fixed over its first 48 pixels, so a
 * region scrolled up to that edge sat under the bar — the review table's dot on the bar itself, and
 * the header's approve and export buttons behind it after the table's foot had scrolled the page down.
 */
function uncoveredTop(frame: Window, scroller: HTMLElement, view: Rect, x: number): number {
  const doc = frame.document
  if (typeof doc.elementFromPoint !== "function") return view.top
  const probeX = Math.min(Math.max(x, view.left + 1), view.left + view.width - 1)
  const limit = view.top + Math.min(view.height / 3, COVER_PROBE_MAX)
  for (let y = view.top; y < limit; y += COVER_PROBE_STEP) {
    const hit = doc.elementFromPoint(probeX, y)
    if (!hit || hit === scroller || scroller.contains(hit)) return y
  }
  return view.top
}

/**
 * Whether `el`, or anything between it and `scroller`, is pinned in place — `fixed` anywhere, or
 * `sticky` inside the scroller. Both stay put while the page scrolls under them, which is what makes
 * them able to cover an edge.
 */
function isPinned(frame: Window, el: Element | null, scroller: HTMLElement): boolean {
  for (let node: Element | null = el; node && node !== scroller; node = node.parentElement) {
    const position = frame.getComputedStyle(node).position
    if (position === "fixed" || position === "sticky") return true
  }
  return false
}

/**
 * The mirror of {@link uncoveredTop} for the bottom edge: the last line of `view` that is not under
 * something pinned over it.
 *
 * Different test, for a different kind of cover. What covers the top is the app bar, *outside* the
 * scroller — so "is this the scroller or something in it" answers it. What covers the bottom is the
 * form's own save bar, which sticks to the bottom *inside* the scroller (`.cfoot` on the entity
 * pages), so containment says nothing and the question is whether what is drawn there stays put while
 * the page scrolls. Without this, a step on the last field of a long form — the employee's
 * Arbeitgeberanteil, a settings section — was scrolled "just inside the bottom edge", which is behind
 * the save bar.
 */
function uncoveredBottom(frame: Window, scroller: HTMLElement, view: Rect, x: number): number {
  const doc = frame.document
  const bottom = view.top + view.height
  if (typeof doc.elementFromPoint !== "function") return bottom
  const probeX = Math.min(Math.max(x, view.left + 1), view.left + view.width - 1)
  const limit = bottom - Math.min(view.height / 3, COVER_PROBE_MAX)
  for (let y = bottom - 1; y > limit; y -= COVER_PROBE_STEP) {
    if (!isPinned(frame, doc.elementFromPoint(probeX, y), scroller)) return y + 1
  }
  return bottom
}

/**
 * The part of `scroller` the reader can see: its box, clipped to the frame's viewport, below whatever
 * is fixed over its top edge and above whatever is pinned over its bottom one. A scroller entirely off
 * screen — an inner one, before the page has been scrolled to it — is taken as it is: there is nothing
 * to clip it to yet.
 */
function visibleView(frame: Window, scroller: HTMLElement, x: number): Rect {
  const box = toRect(scroller.getBoundingClientRect())
  const clipped = intersect(box, viewportRect(frame))
  if (clipped.width <= 0 || clipped.height <= 0) return box
  const top = uncoveredTop(frame, scroller, clipped, x)
  const bottom = uncoveredBottom(frame, scroller, clipped, x)
  const { left, right } = scrollPadding(frame, scroller)
  return {
    ...clipped,
    left: clipped.left + left,
    width: Math.max(0, clipped.width - left - right),
    top,
    height: Math.max(0, bottom - top),
  }
}

/**
 * The strips at a scroller's sides that something pinned covers, as the scroller declares them in CSS
 * `scroll-padding` — the standard's own way of saying "do not scroll anything under here", which the
 * browser's scroll-into-view honours for keyboard focus too.
 *
 * Read rather than probed, unlike the pinned header and footer above: a column stuck to a side is a
 * different element on every row, and the grid it belongs to knows its widths. The review matrix pins
 * its names to the left and four figures to the right, and without this a cell brought "into view" was
 * scrolled right underneath them — the pointer then pressed *Stand* while the cell it meant was hidden
 * below it.
 */
function scrollPadding(frame: Window, scroller: HTMLElement): { left: number; right: number } {
  const style = frame.getComputedStyle(scroller)
  const px = (value: string) => (value.endsWith("px") ? Number.parseFloat(value) || 0 : 0)
  return { left: px(style.scrollPaddingLeft), right: px(style.scrollPaddingRight) }
}

/**
 * Where a scroller has to be for `el` to sit comfortably inside what the reader can see of it, or
 * null if it already does.
 *
 * The least movement that does it — `el` is brought just inside the edge it was beyond, not centred.
 * Centring moved the page further than anything needed, and it provoked the app into moving it back:
 * on Meine Zeiten, a click on a project scrolls the list into view with `block: "nearest"`, so centring
 * the project row first pushed the list's top out of sight and the app then scrolled back up to it —
 * the page jumped twice for one click. An element taller than the scroller is aligned by its top.
 */
function scrollTargetFor(
  frame: Window,
  el: HTMLElement,
  scroller: HTMLElement,
  { roomBelow = false }: ScrollRoom = {}
): { top: number; left: number } | null {
  // A pinned element is already where it is going to be: the save bar at the foot of a form does not
  // move when its scroller does, so scrolling towards it only moves the form behind it.
  if (isPinned(frame, el, scroller)) return null
  const box = toRect(el.getBoundingClientRect())
  const view = visibleView(frame, scroller, box.left + box.width / 2)

  const shift = (start: number, size: number, viewStart: number, viewSize: number, endMargin = SCROLL_MARGIN) => {
    const before = start - (viewStart + SCROLL_MARGIN)
    const after = start + size - (viewStart + viewSize - endMargin)
    if (before < 0) return before // beyond the start edge: bring its start just inside
    if (after > 0) return Math.min(after, before) // beyond the end edge: its end inside, never its start out
    return 0
  }

  const below = roomBelow
    ? Math.max(SCROLL_MARGIN, Math.min(TEXT_ROOM_MAX, view.height * TEXT_ROOM_SHARE))
    : SCROLL_MARGIN
  const dy = shift(box.top, box.height, view.top, view.height, below)
  const dx = shift(box.left, box.width, view.left, view.width)
  if (Math.abs(dy) < 1 && Math.abs(dx) < 1) return null

  return {
    top: Math.max(0, Math.min(scroller.scrollTop + dy, scroller.scrollHeight - scroller.clientHeight)),
    left: Math.max(0, Math.min(scroller.scrollLeft + dx, scroller.scrollWidth - scroller.clientWidth)),
  }
}

/**
 * Every element that scrolls `el`, innermost first — a table row sits in the table's own sideways
 * scroller, which sits in the page. Bringing a row into view is a job for both: the inner one alone
 * cannot move it down the page.
 */
function scrollersOf(frame: Window, el: HTMLElement): HTMLElement[] {
  const chain: HTMLElement[] = []
  for (let scroller = resolveScroller(frame, el); scroller; scroller = resolveScroller(frame, scroller)) {
    chain.push(scroller)
  }
  return chain
}

/** Whether `el` would have to be scrolled to sit comfortably inside its scroll containers. */
export function needsScroll(frame: Window, el: HTMLElement, room: ScrollRoom = {}): boolean {
  return scrollersOf(frame, el).some(scroller => scrollTargetFor(frame, el, scroller, room) !== null)
}

const easeInOutQuad = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2)

/**
 * Brings `el` into view inside the frame, gliding rather than jumping when `durationMs` is given.
 *
 * The glide is ours, driven frame by frame, and never the browser's `behavior: "smooth"` — that one
 * is still animating when the caller measures, so the dot lands next to the element instead of on
 * it. Ours resolves only once it has arrived, so the measurement after it is of a box that is where
 * it is going to stay.
 *
 * `scrollIntoViewIfNeeded` runs afterwards because it covers containers this walk does not reach,
 * notably Vuetify's `show-arrows` tab strip, which slides by `transform` rather than by scrolling.
 */
export async function scrollAnchorIntoView(
  frame: Window,
  el: HTMLElement,
  durationMs = 0,
  shouldStop: () => boolean = () => false,
  room: ScrollRoom = {}
): Promise<void> {
  const chain = scrollersOf(frame, el)

  if (!chain.length) el.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" })
  // Innermost first, each measured afresh: moving the inner one moves `el` within the outer one.
  for (const scroller of chain) {
    if (shouldStop()) return
    const target = scrollTargetFor(frame, el, scroller, room)
    if (target) await glideScroller(frame, scroller, target, durationMs, shouldStop)
  }

  const anyEl = el as HTMLElement & { scrollIntoViewIfNeeded?: (_centre?: boolean) => void }
  anyEl.scrollIntoViewIfNeeded?.(true)
}

/** Whether the scroller `el` sits in is scrolled down at all. False when nothing around it scrolls. */
export function isScrolledDown(frame: Window, el: HTMLElement): boolean {
  const scroller = resolveScroller(frame, el)
  return scroller !== null && scroller.scrollTop > 0
}

/**
 * Scrolls the scroller `el` sits in back to its top, gliding like {@link scrollAnchorIntoView} —
 * for a demonstration that has to start from the page as it looks on arrival.
 */
export async function scrollScrollerToTop(
  frame: Window,
  el: HTMLElement,
  durationMs = 0,
  shouldStop: () => boolean = () => false
): Promise<void> {
  const scroller = resolveScroller(frame, el)
  if (!scroller || scroller.scrollTop <= 0) return
  await glideScroller(frame, scroller, { top: 0, left: scroller.scrollLeft }, durationMs, shouldStop)
}

/** Moves `scroller` to `target`, at once or over `durationMs`, stopping early when `shouldStop` says so. */
async function glideScroller(
  frame: Window,
  scroller: HTMLElement,
  target: { top: number; left: number },
  durationMs: number,
  shouldStop: () => boolean
): Promise<void> {
  if (durationMs <= 0) {
    scroller.scrollTop = target.top
    scroller.scrollLeft = target.left
    return
  }
  const fromTop = scroller.scrollTop
  const fromLeft = scroller.scrollLeft
  const started = performance.now()
  await new Promise<void>(resolve => {
    const tick = () => {
      // Overtaken by another move: stop where we are and let that one take the scroller.
      if (shouldStop()) {
        resolve()
        return
      }
      const t = Math.min(1, (performance.now() - started) / durationMs)
      const eased = easeInOutQuad(t)
      scroller.scrollTop = fromTop + (target.top - fromTop) * eased
      scroller.scrollLeft = fromLeft + (target.left - fromLeft) * eased
      // Wall-clock, not frame count: a throttled frame must not stretch the glide forever.
      if (t >= 1) resolve()
      else void nextFrameOf(frame).then(tick)
    }
    void nextFrameOf(frame).then(tick)
  })
}

/**
 * The frame's next paint — or 50ms of the **host's** clock, whichever is first. A frame that is removed
 * (the walkthrough closed, the frame reloaded) never paints again and its own timers die with it; a
 * wait polling on it alone never settled, and the move stuck in it held every later move behind it.
 */
const nextFrameOf = (frame: Window) =>
  new Promise<void>(resolve => {
    try {
      frame.requestAnimationFrame(() => resolve())
    } catch {
      // A frame already detached may refuse; the host's timer below still answers.
    }
    window.setTimeout(resolve, 50)
  })

/**
 * Resolves once `el`'s box has stopped moving, so nothing is measured mid-transition. Adapted from
 * `waitForStableBox` in `marketing/lib/pointer.mjs`.
 *
 * The deadline is wall-clock rather than a frame count, and the frame's own `requestAnimationFrame`
 * is used: a hidden or background iframe throttles it to nothing, and the walkthrough must not hang.
 */
export function waitForStableRect(frame: Window, el: HTMLElement, timeoutMs = STABLE_TIMEOUT_MS): Promise<Rect> {
  return new Promise(resolve => {
    const deadline = performance.now() + timeoutMs
    let previous: Rect | null = null
    let still = 0

    const tick = () => {
      const rect = toRect(el.getBoundingClientRect())
      const same =
        previous !== null &&
        Math.abs(previous.top - rect.top) < 0.5 &&
        Math.abs(previous.left - rect.left) < 0.5 &&
        Math.abs(previous.width - rect.width) < 0.5 &&
        Math.abs(previous.height - rect.height) < 0.5

      still = same ? still + 1 : 0
      previous = rect

      if (still >= STABLE_FRAMES || performance.now() >= deadline) resolve(rect)
      else void nextFrameOf(frame).then(tick)
    }

    void nextFrameOf(frame).then(tick)
  })
}

/**
 * Resolves once no opacity animation is running on `el` or on anything around it — so a region that
 * is fading in is not refused as `hidden` for the frames it starts at opacity 0.
 *
 * `LewBaseLayout` fades every page's content in on mount (`.page-enter` in `globals.scss`, `backwards`
 * fill, so it *starts* at 0). A step on a page the frame has just opened finds its anchor at once, and
 * {@link waitForStableRect} is no help: a fade does not move the box, so the rect is "stable" while
 * the whole content region is still invisible, and `checkVisibility({ checkOpacity })` refuses it.
 * The first step of the packets tour lost exactly that race. Only finite animations that touch
 * `opacity` count, so a spinner somewhere up the tree cannot hold the walkthrough.
 *
 * The timeout runs on the **host's** clock, for the reason {@link nextFrameOf} does: a frame whose
 * document unloads mid-wait takes its own timers and its animations' `finished` promises with it, and a
 * wait on those alone never settled — leaving the move stuck and every later move queued behind it.
 */
export async function waitForFadeIn(frame: Window, el: HTMLElement, timeoutMs = FADE_TIMEOUT_MS): Promise<void> {
  const doc = frame.document
  if (typeof doc.getAnimations !== "function") return
  const fades = doc.getAnimations().filter(animation => {
    // Duck-typed: the frame has its own `KeyframeEffect`, so `instanceof` against the host's is false.
    const effect = animation.effect as KeyframeEffect | null
    if (typeof effect?.getKeyframes !== "function" || animation.playState !== "running") return false
    const target = effect.target
    if (!target || !target.contains(el)) return false
    if (effect.getComputedTiming().endTime === Infinity) return false
    return effect.getKeyframes().some(keyframe => "opacity" in keyframe)
  })
  if (!fades.length) return
  await Promise.race([
    Promise.allSettled(fades.map(animation => animation.finished)),
    new Promise(resolve => window.setTimeout(resolve, timeoutMs)),
  ])
}

/**
 * Waits for `el` to be pointable — or to fail for a reason other than `hidden`. First for any fade
 * already running ({@link waitForFadeIn}); then, while it is still refused as `hidden`, frame by frame
 * up to `timeoutMs`.
 *
 * The second half is for a transition that has not started yet. A Vue `<Transition>` puts its
 * `-enter-from` class on first — opacity 0 — and the CSS transition only begins a frame later, so for
 * that frame there is no animation to wait for and the element is simply invisible. A replay at speed
 * lands there: the billing wizard's date picker (`dp-menu-appear-*`) was refused seven times in a row
 * on the day it had just been opened to pick, and the customer's assign dialog on its result list.
 * Every other reason — off screen, too small, detached — is answered at once.
 */
export async function waitForVisible(
  frame: Window,
  el: HTMLElement,
  timeoutMs = FADE_TIMEOUT_MS,
  stop: () => boolean = () => false
): Promise<boolean> {
  await waitForFadeIn(frame, el, timeoutMs)
  const deadline = performance.now() + timeoutMs
  for (;;) {
    const rejection = anchorRejection(frame, el)
    if (rejection === null) return true
    if (rejection !== "hidden" || stop() || performance.now() >= deadline) return false
    await nextFrameOf(frame)
  }
}

/**
 * Waits for a step's anchor to appear in the frame, and gives up at `timeoutMs`.
 *
 * Resolves with everything found, visible or not — the caller needs the difference: an anchor that
 * never appeared is a region that is not on this page, while one that appeared and failed
 * {@link isAnchorVisible} is a bug worth warning about. `stop` ends the wait early — for a move that
 * has been overtaken, whose answer nobody is waiting for any more.
 */
export function waitForAnchors(
  frame: Window,
  anchor: ViewHelpAnchor,
  timeoutMs: number,
  stop: () => boolean = () => false
): Promise<HTMLElement[]> {
  return new Promise(resolve => {
    if (!anchor) {
      resolve([])
      return
    }

    const found = findAnchors(frame, anchor)
    if (found.length) {
      resolve(found)
      return
    }

    const deadline = performance.now() + timeoutMs
    const tick = () => {
      const elements = findAnchors(frame, anchor)
      if (elements.length || performance.now() >= deadline || stop()) resolve(elements)
      else void nextFrameOf(frame).then(tick)
    }
    void nextFrameOf(frame).then(tick)
  })
}

/**
 * The smallest rectangle covering all of `elements` — a step may name several anchors, and the two
 * date fields on the analytics view are one region with a gap in the middle.
 */
export function unionRect(elements: HTMLElement[]): Rect | null {
  if (!elements.length) return null

  let top = Infinity
  let left = Infinity
  let right = -Infinity
  let bottom = -Infinity

  for (const el of elements) {
    const box = el.getBoundingClientRect()
    top = Math.min(top, box.top)
    left = Math.min(left, box.left)
    right = Math.max(right, box.right)
    bottom = Math.max(bottom, box.bottom)
  }

  return { top, left, width: right - left, height: bottom - top }
}

/** Where a step's region is right now: the rectangle around those of its anchors that can be pointed at. */
export function visibleRegion(frame: Window, anchor: ViewHelpAnchor): Rect | null {
  return unionRect(findAnchors(frame, anchor).filter(el => isAnchorVisible(frame, el)))
}
